import { Activity, Radio, AlertTriangle, MessageSquare } from "lucide-react";
import CronHealthPanel from "@/components/CronHealthPanel";
import SyncroWebhookHealthPanel from "@/components/SyncroWebhookHealthPanel";
import WholesaleGateDropsPanel from "@/components/WholesaleGateDropsPanel";
import CommentsSyncHealthTile from "@/components/CommentsSyncHealthTile";

/**
 * Admin-only Sync Health dashboard.
 *
 * Aggregates the four pipeline-health surfaces so a silent regression on
 * any of them surfaces from a single page — instead of the admin having
 * to remember to check four different tabs.
 *
 *   1. Cron health — did the nightly backfill / price-sync / auto-payout
 *      actually fire?
 *   2. Syncro webhook health — are payments arriving live?
 *   3. Wholesale-gate drops — are invoice lines being silently excluded?
 *   4. Comments → Syncro mirror health — are notes / stage moves
 *      making it into Syncro?
 *
 * Access: gated by ``<RoleGate role="admin">`` at the route level
 * (App.js), so partners never see the link OR the page.
 */
export default function SyncHealth() {
  return (
    <div className="space-y-8" data-testid="sync-health-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold inline-flex items-center gap-1.5">
          <Activity className="w-3.5 h-3.5" /> Pipeline health
        </div>
        <h1 className="font-display text-4xl tracking-tight mt-1">Sync Health</h1>
        <p className="text-sm text-slate-400 max-w-3xl mt-2">
          A single view of everything wired between the portal and Syncro. If any of the four surfaces below turns red or amber, act on it before customers or partners spot it. Admin-only — partners can&rsquo;t see this page.
        </p>
      </header>

      {/* Quick-glance strip so the whole page's status is legible in ~2 seconds. */}
      <section className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        <QuickTile
          icon={Activity}
          label="Cron jobs"
          hint="Backfill / price-sync / auto-payout"
          testid="quick-cron"
        />
        <QuickTile
          icon={Radio}
          label="Syncro webhooks"
          hint="Live payment feed"
          testid="quick-webhook"
        />
        <div data-testid="quick-comments" className="rounded-xl border border-slate-800 bg-slate-900 p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
            <MessageSquare className="w-3 h-3" /> Comments mirror
          </div>
          <div className="text-[11px] text-slate-500 mt-1 mb-3">Notes + stage moves → Syncro</div>
          <CommentsSyncHealthTile />
        </div>
        <QuickTile
          icon={AlertTriangle}
          label="Gate drops"
          hint="Silent commission leaks"
          testid="quick-gate-drops"
        />
      </section>

      <CronHealthPanel />
      <SyncroWebhookHealthPanel />
      <WholesaleGateDropsPanel />
    </div>
  );
}

function QuickTile({ icon: Icon, label, hint, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-slate-800 bg-slate-900 p-4"
    >
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-[11px] text-slate-500 mt-1">{hint}</div>
      <div className="text-[10px] text-slate-600 mt-3 italic">See detail below ↓</div>
    </div>
  );
}
